import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Button, Card, CardHeader, CardBody } from 'src/components/ui';
import { ExtendedForm } from '../FormComponent';
import { Trans } from '@lingui/react/macro';
import { useSideChannel } from '../../util/useSideChannel';
import { useHistory, useLocation } from 'react-router';
import { useIsMobile } from '../../hooks/useIsMobile';

const SearchBar = ({ resource = '', formDefinition, filters, onSearch, onClearSearch }) => {
  const location = useLocation();
  const history = useHistory();

  const [filtersState, setFilterState] = useState(filters);
  const filtersStateRef = useRef(filters);
  const sideChannel = useSideChannel(filtersState);

  useEffect(() => {
    sideChannel.publish(filtersState);
  }, [filtersState, sideChannel]);

  const filterPropRef = useRef(filters);
  useEffect(() => {
    if (Object.keys(filterPropRef.current).length > 0 && Object.keys(filters).length === 0) {
      setFilterState({});
    }

    filterPropRef.current = filters;
  }, [filters]);

  const isMobile = useIsMobile();
  const numFields = useMemo(() => Object.keys(formDefinition?.schema?.properties || {}).length, [formDefinition]);

  // Max 3 columns. Buttons occupy the last column slot in the grid.
  // n=1 → 2 cols (field + btn in one row)
  // n=2 → 3 cols (field field btn in one row)
  // n=3 → 2 cols (row1: f1 f2 / row2: f3 btn)
  // n≥4 → 3 cols
  // mobile → 1 col (each filter on its own row)
  const cols = useMemo(() => {
    if (isMobile) return 1;
    if (numFields <= 2) return numFields + 1;
    if (numFields === 3) return 2;
    return 3;
  }, [isMobile, numFields]);

  // Buttons are alone on their row when all slots in the last row are filled by fields.
  const smallButtons = useMemo(() => numFields > 0 && numFields % cols === 0, [numFields, cols]);

  const btnSize = smallButtons ? 'sm' : undefined;

  const handleSearch = () => {
    onSearch(filtersStateRef.current);
  };

  // RJSF renders this form with tagName="div" (no real <form> element), so the
  // browser's native Enter-to-submit behavior never kicks in. Wire it up manually,
  // skipping TEXTAREA (Enter should insert a newline) and BUTTON (native click already handles it).
  const handleKeyDown = e => {
    if (e.key !== 'Enter' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') return;
    e.preventDefault();
    handleSearch();
  };

  const searchBarAppend = (
    <div
      style={{
        gridColumn: cols,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        paddingBottom: '1rem',
        gap: '0.25rem',
      }}
    >
      <Button
        color="secondary"
        size={btnSize}
        onClick={e => {
          e.preventDefault();
          setFilterState({});
          onClearSearch();
        }}
      >
        <Trans>Clear Filter</Trans>
      </Button>
      <Button
        color="primary"
        size={btnSize}
        type="submit"
        onClick={() => {
          handleSearch();
        }}
      >
        <Trans id="filter.action">Filter</Trans>
      </Button>
    </div>
  );

  return (
    <Card className="search-bar" onClick={e => e.stopPropagation()}>
      {resource ? (
        <CardHeader>
          <Trans>Filter</Trans> {resource}
        </CardHeader>
      ) : null}
      <CardBody onKeyDown={handleKeyDown}>
        <ExtendedForm
          tagName="div"
          formData={filtersState}
          setFormData={formData => {
            filtersStateRef.current = formData;
            setFilterState(formData);
          }}
          {...formDefinition}
          formContext={{ searchBarAppend, currentFilters: filtersState, sideChannel }}
        >
          <div />
        </ExtendedForm>
      </CardBody>
    </Card>
  );
};

export default SearchBar;
